import React, { Ref } from 'react'
import styled from 'styled-components'
import assetLoader from '@utils/internalImageLoader'
import DownIcon from '@public/images/icons/down.png'
import Image from 'next/image'

type InputPropsT = {
  name: string
  label: string
  value: string
  placeholder?: string
  errors?: boolean
  errorLabel?: string | React.ReactElement
  width?: string
  mWidth?: string
  height?: string
  fontSize?: string
  mHeight?: string
  margin?: string
  mMargin?: string
  type?: string
  centerErrorLabel?: boolean
  customIcon?: string
  iconType?: string
  iconClick?: Function
  hideIcon?: boolean
  hideLabel?: boolean
  disabled?: boolean
  themeColor?: string
  className?: string
  handleInputs?: React.ChangeEventHandler<HTMLInputElement>
  onClickProps?: React.MouseEventHandler<HTMLInputElement>
  onKeyUpProps?: React.KeyboardEventHandler<HTMLInputElement>
  onBlurProps?: React.FocusEventHandler<HTMLInputElement>
  onFocusProps?: React.FocusEventHandler<HTMLInputElement>
  autocomplete?: string
  showCloseIcon?: boolean
  onCloseAction?: Function
  dataTestId?: string
  forwardRef?: Ref<HTMLInputElement>
  padding?: string
  color?: string
  isMobile: boolean
  background?: string
}

const InputsWrapper = styled.div<Pick<InputPropsT, 'margin' | 'width'>>`
  width: ${props => props.width || '320px'};
  margin: ${props => props.margin || '0'};

  @media (max-width: 700px) {
    max-width: ${props => props.mWidth || '320px'};
    ${props => (props.mMargin ? `margin: ${props.mMargin}` : '0')};
  }
`

const InputBox = styled.div<
  Pick<InputPropsT, 'hideIcon' | 'height' | 'mHeight' | 'themeColor' | 'showCloseIcon' | 'errors' | 'padding'>
>`
  display: ${props => (props.hideIcon ? 'block' : 'grid')};
  grid-template-columns: ${props => (props.showCloseIcon ? '30px 1fr 30px' : '30px 1fr')};
  border: 1px solid ${props => (props.errors ? '#D83D37' : props.themeColor || '#e7e7f0')};
  box-sizing: border-box;
  border-radius: 8px;
  height: 48px;
  ${props => (props.height ? `height: ${props.height}` : null)};
  padding: ${props => (props.padding ? `${props.padding}` : '0 2px')};

  @media (max-width: 700px) {
    ${props => (props.mHeight ? `height: ${props.mHeight}` : null)};
  }
`

const InputWrapper = styled.div`
  position: relative;
  height: 100%;
`

const InputComponent = styled.input<{
  disabled?: boolean
  color?: string
}>`
  width: 100%;
  height: 100%;
  outline: 0;
  border: none;
  padding-left: 5px;
  font-size: 16px;
  font-weight: 400;

  ${props =>
    props.color &&
    `color: ${props.color};
`};

  ${props =>
    props.disabled &&
    `background: none;
`};

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }
`

const IconWrapper = styled.div<Pick<InputPropsT, 'hideIcon'>>`
  display: flex;
  justify-content: center;
  align-items: center;
  display: ${props => (props.hideIcon ? 'none' : 'flex')};
`

const CloseIconWrapper = styled(IconWrapper)`
  margin-right: 16px;
  cursor: pointer;
`

const Icon = styled(Image)<Pick<InputPropsT, 'iconType'>>`
  height: ${props => (props.iconType === 'address' ? '25px' : '20px')};
  width: ${props => (props.iconType === 'mobile' ? '15px' : props.iconType === 'address' ? '25px' : '20px')};
  cursor: ${props => (props.iconType === 'close' ? 'pointer' : 'default')};
`

const Label = styled.span<{ filled: boolean; themeColor?: string }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.themeColor || '#5B5675'};
  position: absolute;
  top: ${props => (props.filled ? '-10px' : '14px')};
  left: ${props => (props.filled ? '0px' : '5px')};
  background: #fff;
  padding: 0 3px;
  transition: 0.3s ease all;
  pointer-events: none;

  ${InputComponent}:focus ~ &,
  ${InputComponent}:valid {
    top: -10px;
    left: 0px;
  }
`

const ErrorLabel = styled.div<Pick<InputPropsT, 'centerErrorLabel' | 'margin'>>`
  font-style: normal;
  font-weight: 400;
  color: #d83d37;
  font-size: 12px;
  line-height: 16px;
  text-align: ${props => (props.centerErrorLabel ? 'center' : 'left')};
  margin: ${props => (props.margin ? `${props.margin}` : '5px')};
`

const icons = {
  mobile: `${assetLoader()}/images/icons/callIcon.png`,
  email: `${assetLoader()}/images/icons/mail.png`,
  profile: `${assetLoader()}/images/icons/profile.png`,
  address: `${assetLoader()}/images/icons/locationIcon.png`,
  search: `${assetLoader()}/images/icons/search.png`,
  back: `${assetLoader()}/images/icons/back-icon.png`,
}

export const Input = (props: InputPropsT) => {
  const {
    name,
    label,
    value,
    placeholder,
    errors,
    errorLabel,
    width,
    mWidth,
    height,
    mHeight,
    margin,
    mMargin,
    type,
    centerErrorLabel,
    customIcon,
    iconType,
    iconClick,
    hideLabel,
    className,
    handleInputs,
    onClickProps,
    onKeyUpProps,
    onBlurProps,
    onFocusProps,
    disabled,
    themeColor,
    showCloseIcon,
    onCloseAction,
    dataTestId,
    forwardRef,
    padding,
    color,
  } = props

  return (
    <InputsWrapper width={width} mWidth={mWidth} margin={margin} mMargin={mMargin}>
      <InputBox
        hideIcon={!iconType}
        height={height}
        mHeight={mHeight}
        themeColor={themeColor}
        errors={errors}
        showCloseIcon={showCloseIcon}
        padding={padding}>
        {iconType && (
          <IconWrapper data-testid={iconType} hideIcon={!iconType} onClick={() => iconClick?.()}>
            <Icon
              iconType={iconType}
              src={customIcon || icons[iconType]}
              height={iconType === 'address' ? '25' : '20'}
              width={iconType === 'mobile' ? '15' : iconType === 'address' ? '25' : '20'}
              alt="Search icon"
            />
          </IconWrapper>
        )}
        <InputWrapper>
          <InputComponent
            className={className}
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={handleInputs}
            type={type}
            onClick={onClickProps}
            onBlur={onBlurProps}
            onFocus={onFocusProps}
            onKeyUp={onKeyUpProps}
            autoComplete="none"
            disabled={disabled}
            data-testid={dataTestId}
            ref={forwardRef}
            color={color}
          />
          {hideLabel ? null : (
            <Label themeColor={themeColor} id="inputLabel" filled={value}>
              {label}
            </Label>
          )}
        </InputWrapper>
        {showCloseIcon && value?.length > 0 && (
          <CloseIconWrapper onClick={onCloseAction} data-testid="inputv2-close">
            <Icon src={`${assetLoader()}/images/icons/close.png`} height="20" width="20" alt="close icon" />
          </CloseIconWrapper>
        )}
      </InputBox>
      {errors ? (
        <ErrorLabel centerErrorLabel={centerErrorLabel} margin={margin}>
          {errorLabel}
        </ErrorLabel>
      ) : null}
    </InputsWrapper>
  )
}

type SelectPropsT = {
  name: string
  value: string
  label?: string
  children: React.ReactElement | React.ReactElement[]
  placeholder?: string
  errors?: string | boolean
  errorLabel?: string
  fontSize?: string
  mWidth?: string
  width?: string
  height?: string
  margin?: string
  className?: string
  onChangeHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onClickProps?: () => {}
  onBlurProps?: () => {}
}

const SelectWrapper = styled.div`
  position: relative;
`

const SelectComponent = styled.select<{ fontSize?: string; height?: string }>`
  width: 100%;
  height: ${props => props.height || '44px'};
  margin: 0 auto;
  outline: 0;
  border: none;
  padding: 0 5px;
  border: 1px solid #d6d9e0;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 2px;
  position: relative;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 10px;
  display: block;
  background: #fff;
  -webkit-appearance: none;
  -moz-appearance: none;

  & ~ div {
    top: -5px;
  }
`

const DownIconWrapper = styled.img`
  height: 6px;
  width: 12px;
  right: 10px;
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
`

export const Select = ({
  name,
  label,
  value,
  placeholder,
  errorLabel,
  width,
  height,
  mWidth,
  margin,
  fontSize,
  className,
  children,
  onChangeHandler,
  onBlurProps,
}: SelectPropsT) => (
  <InputsWrapper width={width} mWidth={mWidth} margin={margin}>
    <SelectWrapper>
      <SelectComponent
        className={className}
        name={name}
        value={value}
        height={height}
        placeholder={placeholder}
        fontSize={fontSize}
        onChange={onChangeHandler}
        onBlur={onBlurProps}>
        {children}
      </SelectComponent>
      {label?.length ? <Label filled={value?.length}>{label}</Label> : null}
      <DownIconWrapper src={DownIcon} />
    </SelectWrapper>
    {errorLabel && <ErrorLabel>{errorLabel}</ErrorLabel>}
  </InputsWrapper>
)
